import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import cn from '@/utils/cn';
import SPADataTable from '../shared/SPADataTable';
import SPAPill from '../shared/SPAPill';
import type { SPAPillTone } from '../shared/SPAPill';
import useAssignees from './useAssignees';
import type { DropRow } from './useAssignees';
import { linkClass } from './gradeSyncPath';

export interface ClassroomRow {
  id: number;
  name: string;
  students: number;
  in_an_lms_course: number;
  /** Students with no LMS identity at all — their absence from a course is missing evidence. */
  never_launched?: number;
  course_id?: string | null;
  course_title?: string | null;
}

export interface CourseRow {
  course_id: string;
  title?: string | null;
  /** The LMS side matched back to ours, with the vote behind it. */
  classroom_match?: { name: string | null; votes: number; of: number; decisive: boolean } | null;
  learners: number | null;
  matched_in_tci?: number;
  not_assigned_in_tci?: number;
  blueprint?: boolean;
  /** Who Canvas has the assignment assigned to in this course; null when it cannot say. */
  lms_assigned?: { everyone: boolean; assigned: number; of: number } | null;
  error?: string;
}

// AIDEV-NOTE: This is a bipartite mapping — TCI classes on one side, LMS courses on the other — and
// every fault the dashboard reports is a property of one edge or one unmatched node. Rendering it as
// three prose blocks meant the reader had to rebuild the graph in their head from sentences. One row
// per edge, plus a row for each node with no edge, says the same thing in a shape you can scan.
type Kind = 'edge' | 'orphan_class' | 'orphan_course';

export interface LaunchCourse {
  course_id: string;
  course_title: string | null;
  students: number;
  last_at: string;
}

interface MapRow {
  key: string;
  kind: Kind;
  classroomId: number | null;
  className: string | null;
  students: number | null;
  reachable: number | null;
  courseId: string | null;
  courseTitle: string | null;
  learners: number | null;
  match: CourseRow['classroom_match'];
  neverLaunched: number | null;
  drop?: DropRow;
  blueprint: boolean;
  unreadable: boolean;
  lmsAssigned: CourseRow['lms_assigned'];
  /** The LMS courses this class's students open the teacher's TCI assignments from (launch events). */
  opensFrom: LaunchCourse[];
  /** Its students open TCI from a course this assignment is not in. Never said when a linked course could not
   *  be read: that course's classes have no course on record, and the one they open from may be it. */
  opensElsewhere: boolean;
}

const STATUS: Record<string, { label: string; tone: SPAPillTone }> = {
  blueprint: { label: 'Accepts and discards', tone: 'error' },
  delivering: { label: 'Can deliver', tone: 'success' },
  no_course: { label: 'Nowhere to post', tone: 'error' },
  partial: { label: 'Some can deliver', tone: 'warning' },
  unassigned: { label: 'Not assigned in TCI', tone: 'warning' },
  unreadable: { label: 'Could not read', tone: 'warning' },
};

const statusOf = (row: MapRow): { label: string; tone: SPAPillTone } => {
  if (row.unreadable) return STATUS.unreadable;
  if (row.kind === 'orphan_class') return STATUS.no_course;
  if (row.kind === 'orphan_course') return row.blueprint ? STATUS.blueprint : STATUS.unassigned;
  if (row.blueprint) return STATUS.blueprint;

  return (row.reachable ?? 0) === (row.students ?? 0) ? STATUS.delivering : STATUS.partial;
};

const buildRows = (
  classrooms: ClassroomRow[],
  courses: CourseRow[],
  drops: DropRow[],
  launchCourses?: Map<number, LaunchCourse[]>,
): MapRow[] => {
  const byCourse = new Map(courses.map(c => [c.course_id, c]));
  const dropFor = new Map(drops.map(d => [d.id, d]));
  const linked = new Set(classrooms.map(c => c.course_id).filter(Boolean));
  const anyUnreadable = courses.some(c => c.error != null);

  const classRows = classrooms.map((room): MapRow => {
    const course = room.course_id == null ? undefined : byCourse.get(room.course_id);

    return {
      blueprint: course?.blueprint === true,
      className: room.name,
      classroomId: room.id,
      courseId: room.course_id ?? null,
      courseTitle: course?.title ?? room.course_title ?? null,
      drop: dropFor.get(room.id),
      key: `class-${room.id}`,
      kind: room.course_id == null ? 'orphan_class' : 'edge',
      learners: course?.learners ?? null,
      lmsAssigned: course?.lms_assigned ?? null,
      match: course?.classroom_match ?? null,
      neverLaunched: room.never_launched ?? null,
      opensElsewhere: room.course_id == null && !anyUnreadable,
      opensFrom: launchCourses?.get(room.id) ?? [],
      reachable: room.in_an_lms_course,
      students: room.students,
      unreadable: course?.error != null,
    };
  });

  // A course no class points at still matters: a Blueprint silently swallowing posts, or a real
  // section full of students TCI never assigned.
  const courseRows = courses.filter(c => !linked.has(c.course_id)).map((course): MapRow => ({
    blueprint: course.blueprint === true,
    className: null,
    classroomId: null,
    courseId: course.course_id,
    courseTitle: course.title ?? null,
    key: `course-${course.course_id}`,
    kind: 'orphan_course',
    learners: course.learners,
    lmsAssigned: course.lms_assigned ?? null,
    match: course.classroom_match ?? null,
    neverLaunched: null,
    opensElsewhere: false,
    opensFrom: [],
    reachable: null,
    students: null,
    unreadable: course.error != null,
  }));

  return [...classRows, ...courseRows];
};

// AIDEV-NOTE: Only ever → or ✕. Grades flow one way, TCI into the LMS, so an arrow pointing back at
// TCI invents a direction that does not exist — it read as the course feeding data into TCI. Both
// unmatched kinds are the same fact, a link that isn't there; which side is empty is what the cells
// either side already say.
const Arrow = ({ kind }: { kind: Kind }) => {
  if (kind === 'edge') return <span className="tw-text-spa-tertiary">→</span>;

  return (
    <span
      className="tw-text-spa-error"
      title={kind === 'orphan_class' ? 'no course to post into' : 'no TCI class maps to this course'}
    >
      ✕
    </span>
  );
};

// AIDEV-NOTE: "No course" is a confident negative, and for most stranded classes the evidence is
// simply missing: a student who has never launched has no LMS identity, so their absence from every
// linked course proves nothing. Saying which it is matters, because the remedies differ — add the
// assignment in the LMS course, versus get the students to launch once so they can be matched at all.
const NoCourse = ({ row }: { row: MapRow }) => {
  const never = row.neverLaunched ?? 0;
  const total = row.students ?? 0;

  return (
    <span className="tw-text-sm tw-text-spa-error">
      No course found
      {never > 0 && (
        <span className="tw-block tw-text-sm tw-text-spa-secondary">
          {never === total
            ? `none of the ${total} have ever launched, so none can be matched`
            : `${never} of ${total} have never launched and cannot be matched`}
        </span>
      )}
    </span>
  );
};

// AIDEV-NOTE: Each column stays on its own side of the mapping — this one is always the LMS. The
// orphan-course note belongs here rather than in the class column, where it read as a description of
// a TCI class. The assignment does have a column in this course, so it is live there and its students
// can launch it; what is missing is any TCI class that posts into it.
const CourseNote = ({ row }: { row: MapRow }) => {
  const students = row.learners ?? 0;

  if (students === 0) {
    return (
      <span className="tw-block tw-text-sm tw-text-spa-secondary">
        No students in it — a template or Blueprint, so posts are accepted and discarded
      </span>
    );
  }

  return (
    <span className="tw-block tw-text-sm tw-text-spa-secondary">
      {`${students} can launch it here, but none of them are assigned this in TCI`}
      {row.match?.decisive === true && (
        <span className="tw-block">
          {`${row.match.votes} of its ${row.match.of} TCI-known students sit in ${row.match.name}`}
        </span>
      )}
    </span>
  );
};

// AIDEV-NOTE: The LMS side matched back to ours, shown only when it disagrees with the class posting
// into the course — agreement is the expected case and repeating it on every row is noise.
//
// Deliberately informational rather than a warning. The match is a plurality vote over the course's
// matched students (roster_service#classroom_for_course), so a course with two matched students out
// of thirteen decides it on two votes. A real disagreement means this class posts into a course whose
// students mostly belong to another — worth seeing, not worth alarming over on that evidence.
const MatchedClass = ({ row }: { row: MapRow }) => {
  const { match } = row;

  if (match?.decisive !== true || match.name === row.className) return null;

  return (
    <span className="tw-block tw-text-sm tw-text-spa-secondary">
      {`${match.votes} of its ${match.of} TCI-known students sit in ${match.name}`}
    </span>
  );
};

// Canvas "Assign To": only said when the assignment is not assigned to the whole course, since that is
// when scores for the rest get refused.
const LmsAssigned = ({ assigned }: { assigned: MapRow['lmsAssigned'] }) => {
  if (assigned == null || assigned.everyone) return null;

  return (
    <span className="tw-block tw-text-sm tw-text-spa-error">
      {`Assigned in Canvas to ${assigned.assigned} of ${assigned.of} students`}
    </span>
  );
};

// AIDEV-NOTE: Launches answer what rosters cannot when a class has nowhere to post: which LMS course
// its students are actually in. That course not being the one this assignment is linked to is usually
// the whole case — the teacher never added the assignment there.
const OpensFrom = ({ row }: { row: MapRow }) => {
  if (row.kind === 'orphan_course') return null;
  if (row.opensFrom.length === 0) return <span className="tw-text-spa-tertiary">No launches</span>;

  const missing = row.opensElsewhere;

  return (
    <>
      {row.opensFrom.map(course => (
        <span key={course.course_id} className="tw-block">
          <span className={missing ? 'tw-text-spa-error' : 'tw-text-spa-primary'}>
            {course.course_title ?? `LMS course ${course.course_id}`}
          </span>
          <span className="tw-text-spa-tertiary">{` · ${course.students}`}</span>
        </span>
      ))}
      {missing && (
        <span className="tw-block tw-text-sm tw-text-spa-secondary">This assignment isn&apos;t in that course</span>
      )}
    </>
  );
};

const CourseCell = ({ row, onSelect }: { row: MapRow; onSelect?: (id: string) => void }) => {
  if (row.courseId == null) return <NoCourse row={row} />;

  return (
    <>
      <button
        className={cn('tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm', linkClass)}
        type="button"
        onClick={() => onSelect?.(row.courseId as string)}
      >
        {row.courseTitle || `Course ${row.courseId}`}
        <span className="tw-block tw-font-mono tw-text-xs tw-text-spa-tertiary">{row.courseId}</span>
      </button>
      <LmsAssigned assigned={row.lmsAssigned} />
      {row.kind === 'orphan_course' ? <CourseNote row={row} /> : <MatchedClass row={row} />}
    </>
  );
};

// AIDEV-NOTE: Not "would lose their grade" — these students have no grade in the LMS to lose, which
// is the whole problem, and beside a "No course found" cell that phrasing reads as a contradiction.
// What removal costs them is the assignment itself, and with it any remaining route for the score
// they have already earned.
const Consequence = ({ drop }: { drop: DropRow }) => {
  if (drop.scored_students === 0) {
    return <span className="tw-text-spa-tertiary">No work done yet — nothing to deliver</span>;
  }
  if (drop.stranded_students === 0) {
    return (
      <span className="tw-text-spa-secondary">
        {`All ${drop.scored_students} with a score already have it in the LMS`}
      </span>
    );
  }

  return (
    <span className="tw-text-spa-error">
      {`${drop.stranded_students} of ${drop.scored_students} have a score that has never reached the LMS`}
    </span>
  );
};

interface ClassMapProps {
  assignmentId: string;
  classrooms: ClassroomRow[];
  courses: CourseRow[];
  /** classroom id => the LMS courses its students launch from */
  launchCourses?: Map<number, LaunchCourse[]>;
  onSelectCourse?: (courseId: string) => void;
}

const column = createColumnHelper<MapRow>();

interface ClassMapMeta {
  onSelectCourse?: (courseId: string) => void;
}

// Module-level, with the course callback on the table's meta: columns built per render give every
// cell a new component type, which remounts the rows on each re-render.
const columns = [
  column.display({
    cell: ({ row: { original: row } }) => (
      <>
        {row.className ?? <span className="tw-text-spa-tertiary">No TCI class</span>}
        {/* Both sides of the map read name over id, so a row can be matched against either
            system without guessing which number is which. */}
        {row.classroomId != null && (
          <span className="tw-block tw-font-mono tw-text-xs tw-text-spa-tertiary">{row.classroomId}</span>
        )}
        {row.drop != null && (
          <span className="tw-block">
            <Consequence drop={row.drop} />
          </span>
        )}
      </>
    ),
    header: 'Class in TCI',
    id: 'class',
  }),
  column.display({
    cell: ({ row: { original: row } }) => row.students ?? <span className="tw-text-spa-tertiary">—</span>,
    header: 'Students',
    id: 'students',
    meta: { align: 'right' },
  }),
  column.display({
    cell: ({ row: { original: row } }) => <Arrow kind={row.kind} />,
    header: () => <span className="tw-sr-only">Link</span>,
    id: 'arrow',
    meta: { align: 'center', width: 1 },
  }),
  column.display({
    cell: ({ row: { original: row }, table }) => (
      <CourseCell row={row} onSelect={(table.options.meta as ClassMapMeta).onSelectCourse} />
    ),
    header: 'LMS course it posts grades into',
    id: 'course',
  }),
  column.display({
    cell: ({ row: { original: row } }) => <OpensFrom row={row} />,
    header: 'LMS course it opens TCI from',
    id: 'opens_from',
  }),
  column.display({
    cell: ({ row: { original: row } }) => (row.kind === 'orphan_course'
      ? <span className="tw-text-spa-tertiary">{`${row.learners ?? 0} in LMS`}</span>
      : (
        <>
          {row.reachable}
          <span className="tw-text-spa-tertiary">{` of ${row.students}`}</span>
        </>
      )),
    header: 'Can receive',
    id: 'reachable',
    meta: { align: 'right' },
  }),
  column.display({
    cell: ({ row: { original: row } }) => <SPAPill tone={statusOf(row).tone}>{statusOf(row).label}</SPAPill>,
    header: 'State',
    id: 'state',
  }),
] as ColumnDef<MapRow, unknown>[];

const ClassMap = ({
  assignmentId, classrooms, courses, launchCourses, onSelectCourse,
}: ClassMapProps) => {
  const { preview } = useAssignees(assignmentId);

  const drops = preview.data?.classrooms ?? [];
  const rows = buildRows(classrooms, courses, drops, launchCourses);

  return (
    <SPADataTable
      columns={columns}
      data={rows}
      defaultSort={[]}
      emptyMessage="No classes or courses on this assignment."
      meta={{ onSelectCourse }}
      rowClassName={row => (row.kind === 'edge' ? '' : 'tw-bg-spa-bg-tertiary')}
    />
  );
};

export default ClassMap;
